/**
 * Alcohol API 타입 정의
 * 어드민 술(Alcohol) 관련 API 스펙
 */

// ============================================
// API 엔드포인트 정의
// ============================================

export const AlcoholApi = {
  /** 술 목록 조회 */
  search: {
    endpoint: '/admin/api/v1/alcohols',
    method: 'GET',
  },
  /** 술 lookup 조회 */
  lookup: {
    endpoint: '/admin/api/v1/alcohols/lookup',
    method: 'GET',
  },
  /** 술 상세 조회 */
  detail: {
    endpoint: '/admin/api/v1/alcohols/:alcoholId',
    method: 'GET',
  },
  /** 술 생성 */
  create: {
    endpoint: '/admin/api/v1/alcohols',
    method: 'POST',
  },
  /** 카테고리 레퍼런스 조회 */
  categoryReference: {
    endpoint: '/admin/api/v1/alcohols/categories/reference',
    method: 'GET',
  },
  /** 알코올 엑셀 템플릿 다운로드 */
  excelTemplate: {
    endpoint: '/admin/api/v1/alcohols/excel/template',
    method: 'GET',
  },
  /** 알코올 엑셀 검증 */
  excelValidate: {
    endpoint: '/admin/api/v1/alcohols/excel/validate',
    method: 'POST',
  },
  /** 알코올 JSON 목록 검증 */
  bulkValidate: {
    endpoint: '/admin/api/v1/alcohols/bulk/validate',
    method: 'POST',
  },
  /** 알코올 목록 일괄 등록 */
  bulkCreate: {
    endpoint: '/admin/api/v1/alcohols/bulk',
    method: 'POST',
  },
  /** 술 삭제 */
  delete: {
    endpoint: '/admin/api/v1/alcohols/:alcoholId',
    method: 'DELETE',
  },
  /** 술 수정 */
  update: {
    endpoint: '/admin/api/v1/alcohols/:alcoholId',
    method: 'PUT',
  },
} as const;

// ============================================
// 공통 타입
// ============================================

/** 카테고리 타입 */
export type AlcoholCategory =
  | 'SINGLE_MALT'
  | 'BLEND'
  | 'BLENDED_MALT'
  | 'BOURBON'
  | 'RYE'
  | 'OTHER';

/** 술 타입 */
export type AlcoholType =
  | 'WHISKY'
  | 'RUM'
  | 'VODKA'
  | 'GIN'
  | 'TEQUILA'
  | 'BRANDY'
  | 'BEER'
  | 'WINE'
  | 'ETC';

/** 어드민 술 정렬 기준 */
export type AdminAlcoholSortType =
  | 'KOR_NAME'
  | 'ENG_NAME'
  | 'KOR_CATEGORY'
  | 'ENG_CATEGORY'
  | 'CREATED_AT'
  | 'UPDATED_AT';

/** 정렬 방향 */
export type SortOrder = 'ASC' | 'DESC';

// ============================================
// API 타입 정의
// ============================================

export interface AlcoholApiTypes {
  /** 술 목록 조회 */
  search: {
    /** 요청 파라미터 */
    params: {
      /** 검색어 (한글/영문 이름 검색) */
      keyword?: string;
      /** 카테고리 필터 */
      category?: AlcoholCategory;
      /** 지역 ID 필터 */
      regionId?: number;
      /** 정렬 기준 (기본값: KOR_NAME) */
      sortType?: AdminAlcoholSortType;
      /** 정렬 방향 (기본값: ASC) */
      sortOrder?: SortOrder;
      /** 페이지 번호 (기본값: 0) */
      page?: number;
      /** 페이지 크기 (기본값: 20) */
      size?: number;
      /** 삭제된 데이터 포함 여부 (기본값: false) */
      includeDeleted?: boolean;
    };
    /** 응답 아이템 */
    response: {
      /** 술 ID */
      alcoholId: number;
      /** 한글 이름 */
      korName: string;
      /** 영문 이름 */
      engName: string;
      /** 카테고리 한글명 */
      korCategoryName: string;
      /** 카테고리 영문명 */
      engCategoryName: string;
      /** 이미지 URL */
      imageUrl: string | null;
      /** 생성일시 */
      createdAt: string;
      /** 수정일시 */
      modifiedAt: string;
      /** 삭제일시 (null이면 미삭제) */
      deletedAt: string | null;
    };
    /** 페이지네이션 메타 정보 */
    meta: {
      /** 현재 페이지 */
      page: number;
      /** 페이지 크기 */
      size: number;
      /** 전체 요소 수 */
      totalElements: number;
      /** 전체 페이지 수 */
      totalPages: number;
      /** 다음 페이지 존재 여부 */
      hasNext: boolean;
    };
  };
  /** 술 lookup 조회 */
  lookup: {
    /** 요청 파라미터 */
    params: {
      /** 검색어 (이름, 카테고리, 지역, 증류소 대상) */
      keyword?: string;
      /** 카테고리 그룹 필터 */
      category?: AlcoholCategory | 'ALL';
      /** 지역 ID 필터 */
      regionId?: number;
      /** 증류소 ID 필터 */
      distilleryId?: number;
      /** 페이지 번호 (0부터 시작) */
      page?: number;
      /** 페이지 크기 (기본값: 20, 최대: 100) */
      size?: number;
    };
    /** 응답 아이템 */
    response: {
      /** 술 ID */
      alcoholId: number;
      /** 한글 이름 */
      korName: string;
      /** 영문 이름 */
      engName: string;
      /** 카테고리 한글명 */
      korCategoryName: string;
      /** 카테고리 영문명 */
      engCategoryName: string;
      /** 카테고리 그룹 */
      categoryGroup: AlcoholCategory;
      /** 지역 ID */
      regionId: number | null;
      /** 지역 한글명 */
      korRegion: string | null;
      /** 지역 영문명 */
      engRegion: string | null;
      /** 증류소 ID */
      distilleryId: number | null;
      /** 증류소 한글명 */
      korDistillery: string | null;
      /** 증류소 영문명 */
      engDistillery: string | null;
      /** 이미지 URL */
      imageUrl: string | null;
    };
  };
  /** 술 상세 조회 */
  detail: {
    /** 응답 */
    response: {
      /** 술 ID */
      alcoholId: number;
      /** 한글 이름 */
      korName: string;
      /** 영문 이름 */
      engName: string;
      /** 이미지 URL */
      imageUrl: string | null;
      /** 술 타입 (WHISKY 등) */
      type: string;
      /** 카테고리 한글명 */
      korCategory: string;
      /** 카테고리 영문명 */
      engCategory: string;
      /** 카테고리 그룹 */
      categoryGroup: AlcoholCategory;
      /** 도수 */
      abv: string | null;
      /** 숙성년도 */
      age: string | null;
      /** 캐스크 타입 */
      cask: string | null;
      /** 용량 */
      volume: string | null;
      /** 설명 */
      description: string | null;
      /** 지역 ID */
      regionId: number | null;
      /** 지역 한글명 */
      korRegion: string | null;
      /** 지역 영문명 */
      engRegion: string | null;
      /** 증류소 ID */
      distilleryId: number | null;
      /** 증류소 한글명 */
      korDistillery: string | null;
      /** 증류소 영문명 */
      engDistillery: string | null;
      /** 테이스팅 태그 목록 */
      tastingTags: {
        /** 태그 ID */
        id: number;
        /** 태그 한글명 */
        korName: string;
        /** 태그 영문명 */
        engName: string;
      }[];
      /** 평균 평점 */
      avgRating: number;
      /** 평점 수 */
      totalRatingsCount: number;
      /** 리뷰 수 */
      reviewCount: number;
      /** 찜 수 */
      pickCount: number;
      /** 생성일시 */
      createdAt: string;
      /** 수정일시 */
      modifiedAt: string;
      /** 삭제일시 (null이면 미삭제) */
      deletedAt: string | null;
    };
  };
  /** 술 생성 */
  create: {
    /** 요청 데이터 */
    request: {
      /** 한글 이름 */
      korName: string;
      /** 영문 이름 */
      engName: string;
      /** 도수 (예: "40%") */
      abv: string;
      /** 술 타입 */
      type: AlcoholType;
      /** 카테고리 한글명 */
      korCategory: string;
      /** 카테고리 영문명 */
      engCategory: string;
      /** 카테고리 그룹 */
      categoryGroup: AlcoholCategory;
      /** 지역 ID */
      regionId: number;
      /** 증류소 ID */
      distilleryId: number;
      /** 숙성년도 */
      age: string;
      /** 캐스크 타입 */
      cask: string;
      /** 이미지 URL */
      imageUrl: string;
      /** 설명 */
      description: string;
      /** 용량 (예: "700ml") */
      volume: string;
      /** 연결할 테이스팅 태그 ID 목록 */
      tastingTagIds: number[];
    };
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 생성된 술 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
  /** 술 삭제 */
  delete: {
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 삭제된 술 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
  /** 술 수정 */
  update: {
    /** 요청 데이터 */
    request: {
      /** 한글 이름 */
      korName: string;
      /** 영문 이름 */
      engName: string;
      /** 도수 (예: "40%") */
      abv: string;
      /** 술 타입 */
      type: AlcoholType;
      /** 카테고리 한글명 */
      korCategory: string;
      /** 카테고리 영문명 */
      engCategory: string;
      /** 카테고리 그룹 */
      categoryGroup: AlcoholCategory;
      /** 지역 ID */
      regionId: number;
      /** 증류소 ID */
      distilleryId: number;
      /** 숙성년도 */
      age: string;
      /** 캐스크 타입 */
      cask: string;
      /** 이미지 URL */
      imageUrl: string;
      /** 설명 */
      description: string;
      /** 용량 (예: "700ml") */
      volume: string;
      /** 연결할 테이스팅 태그 ID 목록 */
      tastingTagIds: number[];
    };
    /** 응답 데이터 */
    response: {
      /** 결과 코드 */
      code: string;
      /** 결과 메시지 */
      message: string;
      /** 수정된 술 ID */
      targetId: number;
      /** 응답 시간 */
      responseAt: string;
    };
  };
}

// ============================================
// 헬퍼 타입
// ============================================

/** 술 목록 조회 파라미터 */
export type AlcoholSearchParams = AlcoholApiTypes['search']['params'];

/** 술 목록 아이템 */
export type AlcoholListItem = AlcoholApiTypes['search']['response'];

/** 술 목록 페이지네이션 메타 */
export type AlcoholPageMeta = AlcoholApiTypes['search']['meta'];

/** 술 lookup 조회 파라미터 */
export type AlcoholLookupParams = AlcoholApiTypes['lookup']['params'];

/** 술 lookup 아이템 */
export type AlcoholLookupItem = AlcoholApiTypes['lookup']['response'];

/** 술 상세 정보 */
export type AlcoholDetail = AlcoholApiTypes['detail']['response'];

/** 술에 연결된 테이스팅 태그 */
export type AlcoholTastingTag = AlcoholApiTypes['detail']['response']['tastingTags'][number];

/** 술 생성 요청 데이터 */
export type AlcoholCreateRequest = AlcoholApiTypes['create']['request'];

/** 술 생성 응답 데이터 */
export type AlcoholCreateResponse = AlcoholApiTypes['create']['response'];

/** 술 삭제 응답 데이터 */
export type AlcoholDeleteResponse = AlcoholApiTypes['delete']['response'];

/** 술 수정 요청 데이터 */
export type AlcoholUpdateRequest = AlcoholApiTypes['update']['request'];

/** 술 수정 응답 데이터 */
export type AlcoholUpdateResponse = AlcoholApiTypes['update']['response'];

/** 알코올 Excel 행 단위 오류 또는 경고 */
export interface AlcoholExcelValidationIssue {
  code: string;
  field: string | null;
  message: string;
}

/** 알코올 Excel 행 단위 검증 결과 */
export interface AlcoholExcelValidationRow {
  rowNumber: number;
  clientRowId: string;
  korName?: string | null;
  engName?: string | null;
  abv?: string | null;
  type?: string | null;
  korCategory?: string | null;
  engCategory?: string | null;
  categoryGroup?: string | null;
  region?: string | null;
  distillery?: string | null;
  age?: string | null;
  cask?: string | null;
  description?: string | null;
  volume?: string | null;
  tastingTags?: string | null;
  /** Excel 원문 이미지 파일명(확장자 포함). 빈 값은 null. */
  imageFileName?: string | null;
  regionId?: number | null;
  distilleryId?: number | null;
  tastingTagIds?: number[] | null;
  candidateAlcoholIds?: number[] | null;
  valid: boolean;
  errors: AlcoholExcelValidationIssue[];
  warnings: AlcoholExcelValidationIssue[];
  normalized: AlcoholBulkRowRequest | null;
}

/** 알코올 Excel 업로드 검증 결과 */
export interface AlcoholExcelValidationResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  warningRows: number;
  rows: AlcoholExcelValidationRow[];
}

/** 알코올 벌크 검증·등록 요청 행 */
export interface AlcoholBulkRowRequest {
  clientRowId: string;
  korName: string;
  engName: string;
  abv: string;
  type: string;
  korCategory: string;
  engCategory: string;
  categoryGroup?: string | null;
  regionId: number;
  distilleryId: number;
  age?: string | null;
  cask?: string | null;
  description?: string | null;
  volume: string;
  tastingTagIds?: number[] | null;
  imageUrl?: string | null;
}

/** 알코올 벌크 검증·등록 요청 */
export interface AlcoholBulkRequest {
  rows: AlcoholBulkRowRequest[];
}

/** 알코올 벌크 행 단위 검증 결과 */
export interface AlcoholBulkValidationRow {
  clientRowId: string | null;
  valid: boolean;
  normalized: AlcoholBulkRowRequest | null;
  errors: AlcoholExcelValidationIssue[];
  warnings: AlcoholExcelValidationIssue[];
  candidateAlcoholIds: number[];
}

/** 알코올 JSON 벌크 검증 결과 */
export interface AlcoholBulkValidationResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  warningRows: number;
  rows: AlcoholBulkValidationRow[];
}

/** 알코올 벌크 등록 결과 */
export interface AlcoholBulkCreateResult {
  createdRows: number;
  rows: Array<{
    clientRowId: string;
    alcoholId: number;
  }>;
  validation: AlcoholBulkValidationResult;
}

// ============================================
// 카테고리 그룹 라벨
// ============================================

/** 카테고리 그룹별 한글 라벨 */
export const CATEGORY_GROUP_LABELS: Record<AlcoholCategory, string> = {
  SINGLE_MALT: '싱글몰트',
  BLEND: '블렌디드',
  BLENDED_MALT: '블렌디드 몰트',
  BOURBON: '버번',
  RYE: '라이',
  OTHER: '기타',
};

/** 카테고리 그룹 키 목록 (타입 안전) */
export const ALCOHOL_CATEGORIES: AlcoholCategory[] = Object.keys(
  CATEGORY_GROUP_LABELS
) as AlcoholCategory[];

// ============================================
// 카테고리 레퍼런스 타입
// ============================================

/** 카테고리 레퍼런스 아이템 */
export interface CategoryReference {
  /** 한글 카테고리 */
  korCategory: string;
  /** 영문 카테고리 */
  engCategory: string;
}

/**
 * 카테고리 레퍼런스 응답
 * 서버는 그룹별로 카테고리 목록을 묶어 내려준다.
 */
export type CategoryReferenceMap = Record<AlcoholCategory, CategoryReference[]>;

/** 빈 카테고리 레퍼런스 맵 (로딩 전 폴백) */
export const EMPTY_CATEGORY_REFERENCE_MAP: CategoryReferenceMap = {
  SINGLE_MALT: [],
  BLEND: [],
  BLENDED_MALT: [],
  BOURBON: [],
  RYE: [],
  OTHER: [],
};
